// One connected payroll provider: status, last sync, sync-now (with run
// polling), reconnect on error, disconnect.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Unplug, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

export interface PayrollConnection {
  id: number;
  vendorKey: string;
  status: string;
  vendorEmployerId: string | null;
  lastSyncedAt: string | null;
  lastError: string | null;
  canPushDeductions?: boolean;
  vendor: { slug: string; name: string; logoColor?: string | null; logoInitials?: string | null };
}

interface SyncRun {
  id: number;
  kind: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  recordsCreated: number | null;
  recordsUpdated: number | null;
  error: string | null;
}

const STATUS_BADGES: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  connected: { label: "Connected", variant: "default" },
  syncing: { label: "Syncing…", variant: "secondary" },
  connecting: { label: "Authorizing…", variant: "secondary" },
  pending: { label: "Not connected", variant: "outline" },
  disconnected: { label: "Disconnected", variant: "outline" },
  error: { label: "Error", variant: "destructive" },
};

export default function PayrollConnectionCard({
  connection,
  clientId,
}: {
  connection: PayrollConnection;
  clientId: number;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const connectionsKey = `/api/clients/${clientId}/payroll-connections`;
  const runsKey = `/api/payroll-connections/${connection.id}/sync-runs`;

  const { data: runs = [] } = useQuery<SyncRun[]>({
    queryKey: [runsKey],
    refetchInterval: (query) => {
      const latest = query.state.data?.[0];
      return latest?.status === "running" || connection.status === "syncing" ? 4000 : false;
    },
  });
  const latestRun = runs[0];
  const isRunning = latestRun?.status === "running" || connection.status === "syncing";

  const syncMutation = useMutation({
    mutationFn: async () => apiRequest("POST", `/api/payroll-connections/${connection.id}/sync`, {}),
    onSuccess: () => {
      toast({ title: "Sync started", description: "Employee census is syncing in the background." });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: [runsKey] });
        queryClient.invalidateQueries({ queryKey: [connectionsKey] });
      }, 1000);
    },
    onError: (e: any) => toast({ variant: "destructive", title: "Sync failed to start", description: e?.message }),
  });

  const reconnectMutation = useMutation({
    mutationFn: async () => {
      const returnTo = window.location.pathname + window.location.search;
      const res = await apiRequest("POST", `/api/payroll-connections/${connection.id}/oauth/start`, { returnTo });
      return (await res.json()) as { authUrl: string };
    },
    onSuccess: ({ authUrl }) => window.location.assign(authUrl),
    onError: (e: any) => toast({ variant: "destructive", title: "Failed to reconnect", description: e?.message }),
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => apiRequest("DELETE", `/api/payroll-connections/${connection.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [connectionsKey] });
      toast({ title: "Disconnected", description: "Credentials removed. Employee links are preserved." });
    },
    onError: (e: any) => toast({ variant: "destructive", title: "Failed to disconnect", description: e?.message }),
  });

  // When a running sync finishes, refresh the census + connection queries once.
  const badge = STATUS_BADGES[connection.status] ?? { label: connection.status, variant: "outline" as const };

  return (
    <div className="p-4 border rounded-lg space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-semibold shrink-0"
            style={{ backgroundColor: connection.vendor.logoColor || "#6366f1" }}
          >
            {connection.vendor.logoInitials || connection.vendor.name.slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-medium">{connection.vendor.name}</p>
              <Badge variant={badge.variant}>{isRunning ? "Syncing…" : badge.label}</Badge>
            </div>
            <p className="text-sm text-muted-foreground truncate">
              {connection.lastSyncedAt
                ? `Last synced ${new Date(connection.lastSyncedAt).toLocaleString()}`
                : "Never synced"}
              {latestRun?.status === "completed" &&
                ` · ${latestRun.recordsCreated ?? 0} new / ${latestRun.recordsUpdated ?? 0} updated`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {connection.status === "error" ? (
            <Button variant="outline" size="sm" onClick={() => reconnectMutation.mutate()} disabled={reconnectMutation.isPending}>
              <ExternalLink className="h-4 w-4 mr-2" />
              Reconnect
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending || isRunning || connection.status !== "connected"}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRunning ? "animate-spin" : ""}`} />
              {isRunning ? "Syncing…" : "Sync now"}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => {
              if (window.confirm(`Disconnect ${connection.vendor.name}? Credentials will be removed; employee links are kept.`)) {
                disconnectMutation.mutate();
              }
            }}
            disabled={disconnectMutation.isPending}
          >
            <Unplug className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {connection.lastError && connection.status === "error" && (
        <p className="text-sm text-destructive bg-destructive/5 rounded-md px-3 py-2">{connection.lastError}</p>
      )}
    </div>
  );
}
